import React, { createContext, useContext, useEffect, useState } from 'react';
import io from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const { user } = useAuth();

  useEffect(() => {
    // Get token from localStorage (AuthContext stores it there)
    const token = localStorage.getItem('token');
    
    if (user && token) {
      // Initialize socket connection
      const socketInstance = io(process.env.REACT_APP_API_URL || 'http://localhost:5001', {
        auth: {
          token
        },
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5
      });

      // Connection events
      socketInstance.on('connect', () => {
        setIsConnected(true);
      });

      socketInstance.on('disconnect', () => {
        setIsConnected(false);
      });

      socketInstance.on('connect_error', () => {
        setIsConnected(false);
      });

      // User presence events
      socketInstance.on('user-joined', (data) => {
        setOnlineUsers(prev => new Set([...prev, data.userId]));
      });

      socketInstance.on('user-left', (data) => {
        setOnlineUsers(prev => {
          const newSet = new Set(prev);
          newSet.delete(data.userId);
          return newSet;
        });
      });

      socketInstance.on('presence-update', (data) => {
        if (data.status === 'online') {
          setOnlineUsers(prev => new Set([...prev, data.userId]));
        } else {
          setOnlineUsers(prev => {
            const newSet = new Set(prev);
            newSet.delete(data.userId);
            return newSet;
          });
        }
      });

      // Set initial connected state based on socket's current state
      if (socketInstance.connected) {
        setIsConnected(true);
      }

      setSocket(socketInstance);

      // Cleanup on unmount
      return () => {
        socketInstance.disconnect();
      };
    } else {
      // Disconnect socket if user logs out
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
        setOnlineUsers(new Set());
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [user]); // Only depend on user - token is read from localStorage inside effect

  // Join wedding room
  const joinWedding = (weddingId) => {
    if (socket && isConnected) {
      socket.emit('join-wedding', weddingId);
    }
  };

  // Leave wedding room
  const leaveWedding = (weddingId) => {
    if (socket && isConnected) {
      socket.emit('leave-wedding', weddingId);
    }
  };

  // Send wedding update
  const sendWeddingUpdate = (weddingId, type, payload) => {
    if (socket && isConnected) {
      socket.emit('wedding-update', { weddingId, type, payload });
    }
  };

  // Send guest update
  const sendGuestUpdate = (weddingId, guestId, action, payload) => {
    if (socket && isConnected) {
      socket.emit('guest-update', { weddingId, guestId, action, payload });
    }
  };

  // Send todo update
  const sendTodoUpdate = (weddingId, todoId, action, payload) => {
    if (socket && isConnected) {
      socket.emit('todo-update', { weddingId, todoId, action, payload });
    }
  };

  // Typing indicators
  const startTyping = (weddingId, location) => {
    if (socket && isConnected) {
      socket.emit('typing-start', { weddingId, location });
    }
  };

  const stopTyping = (weddingId, location) => {
    if (socket && isConnected) {
      socket.emit('typing-stop', { weddingId, location });
    }
  };

  // Direct messaging
  const sendMessage = (recipientId, message) => {
    if (socket && isConnected) {
      socket.emit('send-message', { recipientId, message });
    }
  };

  const value = {
    socket,
    isConnected,
    onlineUsers,
    joinWedding,
    leaveWedding,
    sendWeddingUpdate,
    sendGuestUpdate,
    sendTodoUpdate,
    startTyping,
    stopTyping,
    sendMessage
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};